import { JSX } from "react";
import { ProductDetailInfoBar } from "@hatch-digital-nl/bravilor-storybook";
import { ProductDetailInterface } from "../../../common/interfaces/apiV2/product/ProductDetailInterface";
import {
    createMailtoUrl,
    TransformProductDataParams,
    transformProductInfoBarData,
} from "../../../common/utils/productInforBarTransformer";
import { downloadBlob } from "../../../common/utils/Export";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";
import { useExportProductMutation } from "../../../common/api/product";

interface Props {
    data: ProductDetailInterface | undefined;
    isPageLoading: boolean;
    communicationEmail: string | null;
    isDownloading: boolean;
    onDownloadStart: () => void;
    onDownloadEnd: () => void;
}

export const ProductDetailsInfoBar = ({
    data,
    isPageLoading,
    communicationEmail,
    isDownloading,
    onDownloadStart,
    onDownloadEnd,
}: Props): JSX.Element => {
    const { t } = useStringTranslation();
    const [exportProduct] = useExportProductMutation();

    if (isPageLoading || !data) {
        return <ProductDetailInfoBar variant="product-range" isLoading />;
    }

    const paramObject: TransformProductDataParams = {
        data,
        t,
    };
    const productInfoData = transformProductInfoBarData(paramObject);

    const handleDownloadClick = async (): Promise<void> => {
        onDownloadStart();

        try {
            const { blob, contentType } = await exportProduct(data.id).unwrap();

            downloadBlob({
                blob,
                contentType,
                fileName: productInfoData.name,
                extension: "pdf",
            });
        } finally {
            onDownloadEnd();
        }
    };

    return (
        <ProductDetailInfoBar
            variant="product-range"
            name={productInfoData.name}
            identifier={productInfoData.identifier}
            price={data.price?.formatted}
            priceLabel={data.price?.label}
            identifierLabel={productInfoData.labels.identifier}
            breadcrumbs={productInfoData.breadcrumbs}
            showButtons
            requestButtonLabel={productInfoData.labels.requestButton}
            downloadButtonLabel={productInfoData.labels.downloadButton}
            isDownloading={isDownloading}
            onRequestClick={
                communicationEmail
                    ? () => {
                          window.location.href = createMailtoUrl(
                              communicationEmail,
                              t("request-information-subject"),
                              productInfoData.name,
                          );
                      }
                    : undefined
            }
            onDownloadClick={handleDownloadClick}
        />
    );
};
